import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

import { ProfitCenterModal } from './ProfitCenterModal.jsx';
import { ProfitCenterPostingModal } from './ProfitCenterPostingModal.jsx';

export function ProfitCentersPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);

  const report = useQuery({ queryKey: ['finance', 'profit-centers', 'report'], queryFn: () => financeApi.profitCenterReport().then((r) => r.items) });
  const centers = report.data?.centers || [];
  const totals = report.data?.totals;

  const resultCell = (v) => <span className="mono num" style={{ color: Number(v) < 0 ? 'var(--color-danger, #dc2626)' : 'var(--color-success, #059669)' }}>{formatCurrency(v)}</span>;

  const columns = [
    { key: 'code', label: t('pc.col.code'), render: (r) => <span className="mono"><strong>{r.code}</strong></span> },
    { key: 'name', label: t('pc.col.name'), render: (r) => <>{r.name} {!r.isActive && <Badge variant="neutral">{t('pc.inactive')}</Badge>}</> },
    { key: 'revenue', label: t('pc.col.revenue'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.revenue)}</span> },
    { key: 'expense', label: t('pc.col.expense'), align: 'right', render: (r) => <span className="mono num">{formatCurrency(r.expense)}</span> },
    { key: 'result', label: t('pc.col.result'), align: 'right', render: (r) => resultCell(r.result) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('pc.title')}</h1>
          <p className="muted">{t('pc.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="ghost" onClick={() => setPostOpen(true)}>{t('pc.action.post')}</Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('pc.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('pc.metric.revenue')} value={formatCurrency(totals?.revenue || 0)} />
        <MetricCard label={t('pc.metric.expense')} value={formatCurrency(totals?.expense || 0)} />
        <MetricCard label={t('pc.metric.result')} value={formatCurrency(totals?.result || 0)} trendVariant={Number(totals?.result) < 0 ? 'danger' : 'success'} />
      </div>

      <Table columns={columns} rows={centers} isLoading={report.isLoading} emptyMessage={t('pc.empty')} />

      <ProfitCenterModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ProfitCenterPostingModal open={postOpen} onClose={() => setPostOpen(false)} centers={centers} />
    </section>
  );
}
